import React, { useCallback, useState } from "react";
import { CalendarDays, ChevronRight, ImageOff } from "lucide-react";
import {
  BULANDI_CONCLUDED,
  bulandi2026Meta,
  bulandiWorkshopsScheduleDownloadFilename,
  bulandiWorkshopsScheduleImageUrl,
} from "../../data/bulandi2026Data";
import { bulandiWorkshops2026 } from "../../data/bulandiWorkshops2026";
import { partitionBulandiWorkshops } from "../../utils/bulandiWorkshopsPartition";
import {
  BulandiScheduleModal,
  WorkshopDetailsModal,
  WorkshopRegistrationSuccessModal,
} from "./BulandiModals";
import { WorkshopRegistrationDrawer } from "./WorkshopRegistrationDrawer";

const BUL_EVENTS_SECTION_HEADING_CLASS =
  "font-display text-[1.265625rem] sm:text-[1.6875rem] md:text-[2.109375rem] uppercase tracking-tight text-bul-fg";

function GhostCta({ as: Comp = "a", children, className = "", ...props }) {
  return (
    <Comp {...props} className={`bul-cta-ghost ${className}`}>
      {children}
    </Comp>
  );
}

function PrimaryCta({ as: Comp = "button", children, className = "", ...props }) {
  return (
    <Comp {...props} className={`bul-cta-primary ${className}`}>
      {children}
    </Comp>
  );
}

const REGISTRATION_SOON_TITLE = "Registrations will open soon";

const WORKSHOP_GRID_CLASS =
  "grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5";

function WorkshopTile({ ws, onShowDetails, onRegister, past = false }) {
  const [imgErr, setImgErr] = useState(false);
  const showImg = Boolean(ws.detailsImageUrl) && !imgErr;
  const registrationClosed = BULANDI_CONCLUDED || ws.registrationOpen === false;
  const closedLabel = BULANDI_CONCLUDED
    ? bulandi2026Meta.eventConcludedLabel || "Registrations closed"
    : (ws.registrationClosedLabel || "").trim() || REGISTRATION_SOON_TITLE;
  const attendeesLabel = (ws.attendeesLabel || "").trim();

  return (
    <li className="flex flex-col">
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border-2 border-bul-border bg-bul-surface/90 shadow-bul-brutal-sm transition hover:border-bul-lime/50">
        <div
          role="button"
          tabIndex={0}
          className="flex min-h-0 flex-1 cursor-pointer flex-col text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-bul-lime focus-visible:ring-offset-2 focus-visible:ring-offset-bul-bg"
          onClick={() => onShowDetails(ws)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onShowDetails(ws);
            }
          }}
          aria-label={`View details: ${ws.title}`}
        >
          <div className="relative aspect-[4/3] w-full shrink-0 border-b border-bul-border bg-bul-bg/50">
            {showImg ? (
              <img
                src={ws.detailsImageUrl}
                alt={ws.title}
                className="h-full w-full object-cover"
                loading="lazy"
                decoding="async"
                onError={() => setImgErr(true)}
              />
            ) : (
              <div
                className="absolute inset-0 grid place-items-center text-bul-mutedfg/50"
                aria-hidden
              >
                <ImageOff className="h-12 w-12" />
              </div>
            )}
          </div>
          <div className="flex flex-1 flex-col p-4">
            <h3 className="font-display text-[0.75rem] uppercase leading-snug tracking-tight text-bul-fg sm:text-[0.8125rem]">
              {ws.title}
            </h3>
            {past && attendeesLabel ? (
              <p className="mt-1.5 font-mono text-[11px] font-bold tabular-nums text-bul-lime sm:text-xs">
                {attendeesLabel} attendees
              </p>
            ) : (
              <p className="mt-1.5 font-mono text-[11px] font-bold tabular-nums text-bul-cyan sm:text-xs">
                {ws.feeLabel}
              </p>
            )}
            <p className="mt-2 font-mono text-[10px] font-bold uppercase tracking-wider text-bul-lime">
              Tap for details
            </p>
          </div>
        </div>
        {!past ? (
          <div className="border-t border-bul-border p-3 sm:p-4">
            <PrimaryCta
              type="button"
              aria-disabled={registrationClosed ? "true" : undefined}
              tabIndex={registrationClosed ? -1 : undefined}
              aria-label={
                registrationClosed
                  ? `${closedLabel} — ${ws.title}`
                  : `Register for ${ws.title}`
              }
              title={registrationClosed ? closedLabel : undefined}
              className={`flex w-full items-center justify-center gap-2 py-2.5 text-sm ${
                registrationClosed ? "cursor-not-allowed opacity-55 saturate-50" : ""
              }`}
              onClick={(e) => {
                e.stopPropagation();
                if (registrationClosed) return;
                onRegister(ws);
              }}
            >
              {registrationClosed ? closedLabel : "Register Now"}
              <ChevronRight className="h-4 w-4 shrink-0" strokeWidth={3} aria-hidden />
            </PrimaryCta>
          </div>
        ) : null}
      </div>
    </li>
  );
}

function WorkshopSection({ title, workshops, past, onShowDetails, onRegister }) {
  if (!workshops.length) return null;
  return (
    <div className="mb-10 last:mb-0 sm:mb-12">
      <h3 className="mb-5 font-display text-sm uppercase tracking-tight text-bul-mutedfg sm:mb-6 sm:text-base">
        {title}
      </h3>
      <ul className={WORKSHOP_GRID_CLASS}>
        {workshops.map((ws) => (
          <WorkshopTile
            key={ws.id}
            ws={ws}
            past={past}
            onShowDetails={onShowDetails}
            onRegister={onRegister}
          />
        ))}
      </ul>
    </div>
  );
}

export function BulandiWorkshopsTab() {
  const [detailsWorkshop, setDetailsWorkshop] = useState(null);
  const [registerWorkshop, setRegisterWorkshop] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [successWhatsappGroupUrl, setSuccessWhatsappGroupUrl] = useState("");
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);

  const openRegister = useCallback((ws) => {
    if (BULANDI_CONCLUDED) return;
    setRegisterWorkshop(ws);
    setDrawerOpen(true);
  }, []);

  const closeDrawer = useCallback(() => {
    setDrawerOpen(false);
    setRegisterWorkshop(null);
  }, []);

  const { upcoming: upcomingWorkshops, past: pastWorkshops } =
    partitionBulandiWorkshops(bulandiWorkshops2026);

  return (
    <section
      id="workshop-registration"
      role="tabpanel"
      aria-labelledby="bulandi-tab-workshop-registration"
      className="scroll-mt-6 mb-12"
    >
      <header className="mb-8 flex flex-wrap items-end justify-between gap-3 text-center sm:mb-10 sm:text-left">
        <h2 className={BUL_EVENTS_SECTION_HEADING_CLASS}>
          <span className="bul-heading-chrome">Bulandi 2026 · </span>
          <span className="bul-text-gradient-fire">Workshops Catalogue</span>
        </h2>
        {bulandiWorkshopsScheduleImageUrl?.trim() ? (
          <GhostCta
            as="button"
            type="button"
            onClick={() => setScheduleModalOpen(true)}
            className="inline-flex shrink-0 items-center justify-center gap-2"
          >
            <CalendarDays className="h-5 w-5 text-bul-lime" aria-hidden />
            Workshops schedule
            <ChevronRight
              className="h-5 w-5 text-bul-lime"
              aria-hidden
              strokeWidth={2.75}
            />
          </GhostCta>
        ) : bulandi2026Meta.workshopsScheduleUrl ? (
          <GhostCta
            as="a"
            href={bulandi2026Meta.workshopsScheduleUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex shrink-0 items-center justify-center gap-2"
          >
            <CalendarDays className="h-5 w-5 text-bul-lime" aria-hidden />
            Workshops schedule
            <ChevronRight
              className="h-5 w-5 text-bul-lime"
              aria-hidden
              strokeWidth={2.75}
            />
          </GhostCta>
        ) : (
          <button
            type="button"
            disabled
            aria-disabled="true"
            title="Workshops schedule — coming soon"
            className="inline-flex min-h-[52px] shrink-0 cursor-not-allowed items-center justify-center gap-2 rounded-xl border-2 border-dashed border-bul-border bg-bul-bg/60 px-6 py-4 font-mono text-sm font-bold uppercase tracking-wide text-bul-mutedfg"
          >
            <CalendarDays className="h-5 w-5 text-bul-mutedfg" aria-hidden />
            Workshops schedule — coming soon
          </button>
        )}
      </header>

      <WorkshopSection
        title="Upcoming workshops"
        workshops={upcomingWorkshops}
        onShowDetails={setDetailsWorkshop}
        onRegister={openRegister}
      />
      <WorkshopSection
        title="Past workshops"
        workshops={pastWorkshops}
        past
        onShowDetails={setDetailsWorkshop}
        onRegister={openRegister}
      />

      {detailsWorkshop ? (
        <WorkshopDetailsModal
          workshop={detailsWorkshop}
          onClose={() => setDetailsWorkshop(null)}
        />
      ) : null}

      <WorkshopRegistrationDrawer
        open={drawerOpen && !BULANDI_CONCLUDED}
        workshop={registerWorkshop}
        onClose={closeDrawer}
        onSuccess={(whatsappGroupUrl) => {
          setSuccessWhatsappGroupUrl(String(whatsappGroupUrl ?? "").trim());
          setSuccessOpen(true);
        }}
      />

      {successOpen ? (
        <WorkshopRegistrationSuccessModal
          whatsappGroupUrl={successWhatsappGroupUrl}
          onClose={() => {
            setSuccessOpen(false);
            setSuccessWhatsappGroupUrl("");
          }}
        />
      ) : null}

      {scheduleModalOpen ? (
        <BulandiScheduleModal
          imageUrl={bulandiWorkshopsScheduleImageUrl}
          downloadFilename={bulandiWorkshopsScheduleDownloadFilename}
          onClose={() => setScheduleModalOpen(false)}
        />
      ) : null}
    </section>
  );
}
